import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { message, Table, Spin, Input } from "antd";
import { INPUT_SIZE, TABLE_SIZE } from "../../../common/ThemeConstants";
import BasicModalFormComponent from "../../../components/formComponent/BasicModalFormComponent";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";

const initialFormValues = {};

const initialSearchValues = {
    SearchText: "",
};

const columns = [
    { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
    { title: "Sub Category", dataIndex: "SubCategoryName", key: "SubCategoryName" },
    { title: "Category", dataIndex: "CategoryName", key: "CategoryName" },
    { title: "Purchase Unit", dataIndex: "PurchaseUnit", key: "PurchaseUnit" },
    { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
    { title: "Recipe Unit", dataIndex: "RecipeUnit", key: "RecipeUnit" },
];

const InventoryItemBranchParLevel = () => {
    const controller = new window.AbortController();
    const userData = useSelector((state) => state.authReducer);

    const [searchFields, setSearchFields] = useState(initialSearchValues);
    const [itemList, setItemList] = useState([]);
    const [formLoading, setFormLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [updateId, setUpdateId] = useState(null);
    const [shouldCloseModal, setShouldCloseModal] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [branchParLevelData, setBranchParLevelData] = useState([]);
    const [branchParLevelLoading, setBranchParLevelLoading] = useState(false);
    const [selectedItemName, setSelectedItemName] = useState("");
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [branchFilterText, setBranchFilterText] = useState("");
    const [headerBulkValues, setHeaderBulkValues] = useState({
        MinParLevel: "",
        MaxParLevel: "",
    });

    const fetchData = (searchParams = {}, pageNumber = 1, pageSize = 10) => {
        setTableLoading(true);
        const payload = {
            OperationId: 1,
            CompanyId: userData.CompanyId,
            UserId: userData.UserId,
            UserIP: "12.1.1.2",
            ItemId: null,
            SearchText: searchParams.SearchText || "",
            PageNumber: pageNumber,
            PageSize: pageSize,
        };

        postRequest("/Inv_Item", payload, controller)
            .then((response) => {
                setTableLoading(false);
                if (response.error) {
                    message.error(response.errorMessage);
                    return;
                }
                if (response.data.response === false) {
                    message.error(response.data.DataSet?.Table?.errorMessage || "Error fetching data");
                    return;
                }

                const dataSet = response.data;
                setItemList(dataSet.Data || []);
                setPagination((prev) => ({
                    ...prev,
                    current: pageNumber,
                    pageSize: pageSize,
                    total: dataSet?.TotalCount || dataSet?.TotalRecords || dataSet?.Data?.length || 0,
                }));

            })
            .catch((error) => {
                setTableLoading(false);
                if (error.message !== "canceled") {
                    console.error("Error fetching items:", error);
                    message.error("Failed to fetch items");
                }
            });
    };

    useEffect(() => {
        fetchData({}, pagination.current, pagination.pageSize);
        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (updateId === null) return;

        const selectedItem = itemList.find((item) => item.ItemId === updateId);
        if (!selectedItem) {
            setUpdateId(null);
            return;
        }

        setSelectedItemId(updateId);
        setSelectedItemName(selectedItem.ItemName || "");
        fetchBranchParLevelData(updateId);
        setUpdateId(null);
    }, [updateId, itemList]);

    const handleSearchChange = (data) => {
        setSearchFields((prev) => ({ ...prev, [data.name]: data.value }));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const trimmedSearch = { ...searchFields, SearchText: searchFields.SearchText?.trim() || "" };
        setSearchFields(trimmedSearch);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchData(trimmedSearch, 1, pagination.pageSize);
    };

    const handleDeleteRow = (id) => {
        const payload = {
            OperationId: 4,
            CompanyId: userData.CompanyId,
            UserId: userData.UserId,
            UserIP: "12.1.1.2",
            ItemId: id,
        };

        postRequest("/Inv_ItemBranchParLevel", payload, controller)
            .then((response) => {
                if (response.error) {
                    message.error(response.errorMessage);
                    return;
                }
                if (response.data.response === false) {
                    message.error(response.data.DataSet?.Table?.errorMessage || "Error deleting item");
                    return;
                }
                message.success(response.successMessage || "Item Branch Par Level Deleted Successfully and Set to Default");
                fetchData(searchFields, pagination.current, pagination.pageSize);
            })
            .catch((error) => {
                if (error.message !== "canceled") {
                    console.error("Error deleting item:", error);
                    message.error("Failed to delete item");
                }
            });
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();

        // Validate branch par level data
        if (!validateBranchParLevelFields()) {
            message.error("Please fill all Min Par Level and Max Par Level fields");
            return;
        }

        // Check if we have branch par level data to save
        if (!branchParLevelData || branchParLevelData.length === 0) {
            message.error("No branch par level data to save");
            return;
        }

        // Check if we have a selected item
        if (!selectedItemId) {
            message.error("Please select an item first");
            return;
        }

        setFormLoading(true);

        // Prepare ItemBranchParLevels array from branchParLevelData
        const itemBranchParLevels = branchParLevelData.map((item) => ({
            id: item.id || 0,
            BranchId: item.BranchId,
            RecipeUnitId: item.RecipeUnitId || null,
            MinParLevel: parseFloat(item.MinParLevel) || 0,
            MaxParLevel: parseFloat(item.MaxParLevel) || 0,
        }));

        // Prepare payload for Inv_ItemBranchParLevel API
        const payload = {
            OperationId: 2, // 2 for update, 3 for insert
            CompanyId: userData.CompanyId,
            UserId: userData.UserId,
            UserIP: "12.1.1.2",
            ItemId: selectedItemId,
            SearchText: "",
            PageNumber: 1,
            PageSize: 1000,
            ItemBranchParLevels: itemBranchParLevels,
        };

        postRequest("/Inv_ItemBranchParLevel", payload, controller)
            .then((response) => {
                setFormLoading(false);

                if (response.error) {
                    message.error(response.errorMessage || "Error saving branch par level data");
                    return;
                }

                if (response.data.response === false) {
                    message.error(response.data.DataSet?.Table?.errorMessage || response.data.Error_Message || "Error saving branch par level data");
                    return;
                }

                message.success(response.data.Message || response.successMessage || "Branch par level data saved successfully");

                // Refresh the branch par level data
                if (selectedItemId) {
                    fetchBranchParLevelData(selectedItemId);
                }

                // Close modal if needed
                setShouldCloseModal(true);
            })
            .catch((error) => {
                setFormLoading(false);
                if (error.message !== "canceled") {
                    console.error("Error saving branch par level data:", error);
                    message.error("Failed to save branch par level data");
                }
            });
    };

    const handleFormClose = () => {
        setUpdateId(null);
        setShouldCloseModal(false);
        setBranchFilterText("");
        setHeaderBulkValues({ MinParLevel: "", MaxParLevel: "" });
    };

    const handleReset = () => {
        setSearchFields(initialSearchValues);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchData(initialSearchValues, 1, pagination.pageSize);
    };

    const handlePagination = (paginationInfo) => {
        const { current, pageSize } = paginationInfo;
        setPagination((prev) => ({ ...prev, current, pageSize }));
        fetchData(searchFields, current, pageSize);
    };

    const fetchBranchParLevelData = (itemId) => {
        setBranchParLevelLoading(true);
        const payload = {
            OperationId: 1,
            CompanyId: userData.CompanyId,
            UserId: userData.UserId,
            UserIP: "12.1.1.2",
            ItemId: itemId,
            SearchText: "",
            PageNumber: 1,
            PageSize: 1000,
            ItemBranchParLevels: [],
        };

        postRequest("/Inv_ItemBranchParLevel", payload, controller)
            .then((response) => {
                setBranchParLevelLoading(false);
                if (response.error) {
                    message.error(response.errorMessage);
                    return;
                }
                if (response.data.response === false) {
                    message.error(response.data.DataSet?.Table?.errorMessage || "Error fetching branch par level data");
                    return;
                }

                const dataSet = response.data;
                setBranchParLevelData(dataSet.Data || []);
                setBranchFilterText("");
                setHeaderBulkValues({ MinParLevel: "", MaxParLevel: "" });
            })
            .catch((error) => {
                setBranchParLevelLoading(false);
                if (error.message !== "canceled") {
                    console.error("Error fetching branch par level data:", error);
                    message.error("Failed to fetch branch par level data");
                }
            });
    };


    const handleBranchParLevelChange = (recordId, field, value) => {
        setBranchParLevelData((prevData) =>
            prevData.map((item) =>
                (item.id === recordId || item.BranchId === recordId)
                    ? { ...item, [field]: value }
                    : item
            )
        );

        // Clear validation error when user starts typing
        const errorKey = `${recordId}_${field}`;
        if (validationErrors[errorKey]) {
            setValidationErrors((prev) => {
                const updated = { ...prev };
                delete updated[errorKey];
                return updated;
            });
        }
    };

    const validateBranchParLevelFields = () => {
        const errors = {};
        branchParLevelData.forEach((item) => {
            const recordId = item.id || item.BranchId;
            if (!item.MinParLevel || item.MinParLevel === "" || item.MinParLevel === null || item.MinParLevel === undefined) {
                errors[`${recordId}_MinParLevel`] = "Min Par Level is required";
            }
            if (!item.MaxParLevel || item.MaxParLevel === "" || item.MaxParLevel === null || item.MaxParLevel === undefined) {
                errors[`${recordId}_MaxParLevel`] = "Max Par Level is required";
            }
        });
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleBlur = (recordId, field, value) => {
        const errorKey = `${recordId}_${field}`;
        if (!value || value === "" || value === null || value === undefined) {
            setValidationErrors((prev) => ({
                ...prev,
                [errorKey]: `${field === "MinParLevel" ? "Min Par Level" : "Max Par Level"} is required`,
            }));
        } else {
            setValidationErrors((prev) => {
                const updated = { ...prev };
                delete updated[errorKey];
                return updated;
            });
        }
    };

    const getFilteredBranchParLevelData = () => {
        const trimmedFilter = branchFilterText.trim().toLowerCase();
        if (!trimmedFilter) return branchParLevelData;
        return branchParLevelData.filter((item) => (item.BranchName || "").toLowerCase().includes(trimmedFilter));
    };

    const applyHeaderValueToRows = (field, value) => {
        const filteredRows = getFilteredBranchParLevelData();
        const targetRecordIds = new Set(filteredRows.map((item) => item.id || item.BranchId));

        setBranchParLevelData((prevData) =>
            prevData.map((item) => {
                const recordId = item.id || item.BranchId;
                return targetRecordIds.has(recordId) ? { ...item, [field]: value } : item;
            })
        );

        if (value !== "" && value !== null && value !== undefined) {
            setValidationErrors((prev) => {
                const updated = { ...prev };
                targetRecordIds.forEach((recordId) => {
                    delete updated[`${recordId}_${field}`];
                });
                return updated;
            });
        }
    };

    const handleHeaderBulkChange = (field, value) => {
        setHeaderBulkValues((prev) => ({ ...prev, [field]: value }));
        applyHeaderValueToRows(field, value);
    };

    const branchParLevelColumns = [
        {
            title: (
                <div>
                    <div>Branch</div>
                    <Input
                        placeholder="Filter branch"
                        value={branchFilterText}
                        onChange={(e) => setBranchFilterText(e.target.value)}
                        allowClear
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            ),
            dataIndex: "BranchName",
            key: "BranchName"
        },
        {
            title: (
                <div>
                    <div>Min Par Level</div>
                    <Input
                        type="number"
                        placeholder="Copy to rows"
                        value={headerBulkValues.MinParLevel}
                        onChange={(e) => handleHeaderBulkChange("MinParLevel", e.target.value)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            ),
            dataIndex: "MinParLevel",
            key: "MinParLevel",
            render: (text, record) => {
                const recordId = record.id || record.BranchId;
                const errorKey = `${recordId}_MinParLevel`;
                const hasError = validationErrors[errorKey];
                return (
                    <div>
                        <Input
                            type="number"
                            value={text || ""}
                            onChange={(e) => handleBranchParLevelChange(recordId, "MinParLevel", e.target.value)}
                            onBlur={(e) => handleBlur(recordId, "MinParLevel", e.target.value)}
                            style={{
                                width: "100%",
                                borderColor: hasError ? "#ff4d4f" : undefined
                            }}
                            required
                        />
                        {hasError && (
                            <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                                {validationErrors[errorKey]}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <div>
                    <div>Max Par Level</div>
                    <Input
                        type="number"
                        placeholder="Copy to rows"
                        value={headerBulkValues.MaxParLevel}
                        onChange={(e) => handleHeaderBulkChange("MaxParLevel", e.target.value)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            ),
            dataIndex: "MaxParLevel",
            key: "MaxParLevel",
            render: (text, record) => {
                const recordId = record.id || record.BranchId;
                const errorKey = `${recordId}_MaxParLevel`;
                const hasError = validationErrors[errorKey];
                return (
                    <div>
                        <Input
                            type="number"
                            value={text || ""}
                            onChange={(e) => handleBranchParLevelChange(recordId, "MaxParLevel", e.target.value)}
                            onBlur={(e) => handleBlur(recordId, "MaxParLevel", e.target.value)}
                            style={{
                                width: "100%",
                                borderColor: hasError ? "#ff4d4f" : undefined
                            }}
                            required
                        />
                        {hasError && (
                            <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                                {validationErrors[errorKey]}
                            </div>
                        )}
                    </div>
                );
            },
        },
        { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
    ];

    const searchPanel = (
        <Fragment>
            <FormTextField
                colSpan={4}
                label="Search"
                name="SearchText"
                size={INPUT_SIZE}
                value={searchFields.SearchText}
                onChange={handleSearchChange}
            />
        </Fragment>
    );

    const formPanel = (
        <Spin spinning={branchParLevelLoading}>
            <Table
                columns={branchParLevelColumns}
                dataSource={getFilteredBranchParLevelData()}
                rowKey={(record) => record.id || record.BranchId}
                className="no-broder-row"
                pagination={false}
                loading={branchParLevelLoading}
                size={TABLE_SIZE}
            />
        </Spin>
    );

    return (
        <Fragment>
            <BasicModalFormComponent
                formTitle="Inventory Branch Item Par Level"
                searchPanel={searchPanel}
                formPanel={formPanel}
                searchSubmit={handleSearchSubmit}
                onFormSave={handleFormSubmit}
                onFormClose={handleFormClose}
                tableRows={itemList}
                tableLoading={tableLoading}
                formLoading={formLoading}
                tableColumn={columns}
                deleteRow={handleDeleteRow}
                actionID="ItemId"
                editRow={setUpdateId}
                fields={initialFormValues}
                reset={false}
                handleReset={handleReset}
                crudTitle="Item"
                externalCloseTrigger={shouldCloseModal}
                onPaginationChange={handlePagination}
                paginationConfig={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
                hideAddButton={true}

            />
        </Fragment>
    );
};

export default InventoryItemBranchParLevel;
